import { useEffect, useRef, useCallback } from "react";

export function useP2PSocket({
  socket,
  userId,
  myChatId,

  // state setters
  setP2pOffers,
  setP2POrders,
  setMyAds,
  setWallet,
  setOrderTimers,
  setSellerCurrency,
  setSellerPaymentDetails,

  showToast,

  // callbacks
  onAdCreated,
  onOrderCreated,
  onOrderCompleted,   // ADD
  onOrderExpired,
  onOrderPaid,
  onActiveOrders,
  onAdDeleted,
  onDeleteAdError,
  onAdUpdated,        // ADD
  onUpdateAdError,     // ADD
  onMyAdLoaded,        // ADD — fired on uswd:get-my-ad:success
  onGetMyAdError,       // ADD — fired on uswd:get-my-ad:error
  onUpdateCurrencySuccess,
  onUpdateCurrencyError,

  onPaymentDetailsSaved,   // ✅ fired when p2p:payment-details-saved arrives

  onFollowSellerResult,
  onNewFollower,
}) {
  /* =========================================================
     REFS
  ========================================================= */
  const hasRequestedActiveOrdersRef = useRef(false);
  const hasRequestedMyAdRef         = useRef(false);   // ADD
  const joinedOrdersRef             = useRef(new Set());
  const orderTimersRef              = useRef({});

  /* =========================================================
     ACTIONS
  ========================================================= */
 const handleConfirmPayment = useCallback(
  (order) => {
    if (!socket || !order?.orderId) return;

    socket.emit("p2p:confirm-payment", {
      orderId: order.orderId,
    });
  },
  [socket]
);

  /* =========================================================
     MAIN SOCKET EFFECT
  ========================================================= */
  useEffect(() => {
    if (!socket || !myChatId) return;

    /* ── Active orders request (idempotent) ── */
    const requestActiveOrders = () => {
      if (hasRequestedActiveOrdersRef.current) return;
      hasRequestedActiveOrdersRef.current = true;

      socket.emit("p2p:get-active-orders", { chatId: myChatId }, (res) => {
        if (!res?.success) return;

        const orders = res.orders || [];

        /* ----------------------------------------
           Merge ACTIVE socket orders with
           HISTORY loaded from HTTP
        ---------------------------------------- */
        setP2POrders?.((prev) => {
          const history = (prev || []).filter(
            (o) => !["pending", "paid"].includes(o.status)
          );
          return [...orders, ...history];
        });

        orders.forEach((order) => {
          if (!order?.orderId) return;
          if (joinedOrdersRef.current.has(order.orderId)) return;

          joinedOrdersRef.current.add(order.orderId);
          socket.emit("p2p:join-order", { orderId: order.orderId });
        });

        onActiveOrders?.(orders);
      });
    };

    /* ── My active USWD ad request (idempotent) ── */          // ADD
    const requestMyAd = () => {                                  // ADD
      if (hasRequestedMyAdRef.current) return;                   // ADD
      hasRequestedMyAdRef.current = true;                        // ADD
      socket.emit("uswd:get-my-ad");                             // ADD
    };                                                            // ADD

 /* ── Order completed ── */
const onOrderCompletedSocket = (data) => {
  const { orderId } = data;
  if (!orderId) return;

  showToast?.("🎉 Order completed successfully");

  setP2POrders?.((prev) =>
    prev.map((o) =>
      o.orderId === orderId
        ? { ...o, status: "completed" }
        : o
    )
  );

  // 🔥 Notify GroupChat immediately so it clears
  // activeP2POrder and hides "Go to Payment" instantly.
  onOrderCompleted?.({
    ...data,
    status: "completed",
  });

  setOrderTimers?.((prev) => {
    const copy = { ...prev };
    delete copy[orderId];
    return copy;
  });

  socket.emit("p2p:committed-update");
};

    requestActiveOrders();
    requestMyAd();   // ADD

    const onConnect = () => {
      hasRequestedActiveOrdersRef.current = false;
      hasRequestedMyAdRef.current = false;   // ADD
      joinedOrdersRef.current.clear();
      requestActiveOrders();
      requestMyAd();   // ADD
    };

    /* ── AD CREATED ── */
   const onAdCreatedSocket = (data) => {
  if (data?.error) {
    showToast?.(data.error);
    onAdCreated?.({ error: true, reason: data.error });
    return;
  }

  if (data?.requirePaymentDetails) {
    onAdCreated?.({ requirePaymentDetails: true });
    return;
  }

  if (data?.ad) {
    setMyAds?.((prev) => [...prev, data.ad]);

    setP2pOffers?.((prev = []) => {
      const list = Array.isArray(prev) ? prev : [];
      const exists = list.some(
        (a) => String(a.adId) === String(data.ad.adId)
      );
      if (exists) return list;
      return [data.ad, ...list];
    });

    socket.emit("p2p:committed-update");
  }

  // Always tell the UI the request succeeded, even if the server
  // response was missing the `ad` object — otherwise P2P.js never
  // receives an AD_CREATED event, "creatingAd" stays stuck true,
  // and the Done screen never shows.
  onAdCreated?.(data);
};

    const onNewAdSocket = (data) => {
      if (!data?.ad) return;

      setP2pOffers?.((prev = []) => {
        const list = Array.isArray(prev) ? prev : [];
        const exists = list.some(
          (a) => String(a.adId) === String(data.ad.adId)
        );
        if (exists) return list;
        return [data.ad, ...list];
      });
    };

    /* ── AD UPDATED ── */
    const onAdUpdatedSocket = ({ ad }) => {
      if (!ad?.adId) return;

      setP2pOffers?.((prev = []) =>
        prev.map((item) =>
          String(item.adId) === String(ad.adId) ? { ...item, ...ad } : item
        )
      );

      setMyAds?.((prev = []) =>
        prev.map((item) =>
          String(item.adId) === String(ad.adId) ? { ...item, ...ad } : item
        )
      );
    };

    /* ── AD DELETED ── */
    const onAdDeletedSocket = ({ adId, success }) => {
      if (!adId) return;

      setP2pOffers?.((prev = []) =>
        prev.filter((ad) => String(ad.adId) !== String(adId))
      );

      setMyAds?.((prev = []) =>
        prev.filter((ad) => String(ad.adId) !== String(adId))
      );

      socket.emit("p2p:committed-update");

      if (success) showToast?.("Ad deleted");

      onAdDeleted?.({ adId, success });
    };

    /* ── DELETE AD ERROR ── */
    const onDeleteAdErrorSocket = ({ error }) => {
      showToast?.(error || "Failed to delete ad");
      onDeleteAdError?.({ error });
    };

    /* ── UPDATE AD SUCCESS ── */
   const onUpdateAdSuccessSocket = (data) => {
  if (data?.error) {
    showToast?.(data.error);
    onUpdateAdError?.({ error: data.error });
    return;
  }

  const ad = data?.ad;

  if (ad?.adId) {
    setP2pOffers?.((prev = []) =>
      prev.map((item) =>
        String(item.adId) === String(ad.adId)
          ? { ...item, ...ad }
          : item
      )
    );

    setMyAds?.((prev = []) =>
      prev.map((item) =>
        String(item.adId) === String(ad.adId)
          ? { ...item, ...ad }
          : item
      )
    );

    showToast?.("✅ Ad updated");
  }

  // Always notify the UI, even if the server response omitted `ad`.
  // This keeps the update flow consistent with USDT and prevents
  // the UI from missing a successful update event.
  onAdUpdated?.(data);
};

    /* ── UPDATE AD ERROR ── */
    const onUpdateAdErrorSocket = ({ error }) => {
      showToast?.(error || "Failed to update ad");
      onUpdateAdError?.({ error });
    };

    /* ── GET MY AD SUCCESS ── */                                          // ADD
    const onGetMyAdSuccessSocket = ({ ad }) => {                           // ADD
      setMyAds?.(ad ? [ad] : []);                                          // ADD
      onMyAdLoaded?.(ad ?? null);                                          // ADD
    };                                                                     // ADD

    /* ── GET MY AD ERROR ── */                                            // ADD
    const onGetMyAdErrorSocket = ({ message }) => {                        // ADD
      onGetMyAdError?.({ error: message });                                // ADD
    };                                                                     // ADD

    /* ── REQUIRE PAYMENT DETAILS (server-side guard) ── */
    const onRequirePaymentDetails = () => {
      onAdCreated?.({ requirePaymentDetails: true });
    };

    /* ── PAYMENT DETAILS SAVED
         Server emits this AFTER p2p:set-payment-details succeeds.
         We forward it so P2P.js can re-submit the pending ad.
    ── */
    const onPaymentDetailsSavedSocket = (data) => {
      onPaymentDetailsSaved?.(data);
    };

    /* ── COMMITTED UPDATE (server pushes fresh wallet snapshot) ── */
    const onCommittedUpdate = (data) => {
      if (!data) return;

      setWallet?.({
        balance:   data.balance,
        committed: data.committed,
        available: data.available,
      });
    };

    /* ── NEW ORDER (seller side) ── */
    const onNewOrder = (data) => {
      if (!data?.orderId) return;

      setP2POrders?.((prev) => [
        {
          ...data,
          asset:     data.asset,
          status:    "pending",
          createdAt: new Date().toISOString(),
        },
        ...prev,
      ]);

      showToast?.("📦 New P2P order received");

      if (!joinedOrdersRef.current.has(data.orderId)) {
        joinedOrdersRef.current.add(data.orderId);
        socket.emit("p2p:join-order", { orderId: data.orderId });
      }
    };

    /* ── ORDER CREATED (buyer side) ── */
    const onOrderCreatedSocket = (data) => {
      if (data?.error) {
        showToast?.(data.error);
        return;
      }

      if (!data?.orderId) return;

      setP2POrders?.((prev) => [
        {
          orderId:        data.orderId,
          adId:           data.adId,
          amount:         data.amount,
          asset:          data.asset,
          rate:           data.rate,
          currency:       data.currency,
          sellerChatId:   data.sellerChatId,
          sellerUsername: data.sellerUsername,
          buyerChatId:    myChatId,
          status:         "pending",
          createdAt:      new Date().toISOString(),
        },
        ...prev,
      ]);

      if (!joinedOrdersRef.current.has(data.orderId)) {
        joinedOrdersRef.current.add(data.orderId);
        socket.emit("p2p:join-order", { orderId: data.orderId });
      }

      socket.emit("p2p:committed-update");
      onOrderCreated?.(data);
    };

    /* ── LIVE ORDER TIMER ── */
    const onOrderTimer = ({ orderId, remaining }) => {
      if (!orderId) return;

      setOrderTimers?.((prev) => {
        const next = { ...prev, [orderId]: remaining };
        orderTimersRef.current = next;
        return next;
      });
    };

    /* ── ORDER EXPIRED ── */
    const onOrderExpiredSocket = (data) => {
      const { orderId } = data;
      if (!orderId) return;

      showToast?.("⛔ Order expired");

      setP2POrders?.((prev) =>
        prev.map((o) =>
          o.orderId === orderId ? { ...o, status: "expired" } : o
        )
      );

      setOrderTimers?.((prev) => {
        const copy = { ...prev };
        delete copy[orderId];
        return copy;
      });

      onOrderExpired?.(data);
    };

    /* ── ORDER PAID ── */
    const onOrderPaidSocket = ({ orderId, sellerChatId, buyerChatId }) => {
  if (!orderId) return;

  // Show the correct message based on who received the event.
  if (String(myChatId) === String(buyerChatId)) {
    showToast?.("✅ You marked the order as paid.");
  } else if (String(myChatId) === String(sellerChatId)) {
    showToast?.("💰 Buyer marked the order as paid. Please confirm payment.");
  }

  setP2POrders?.((prev) => {
    if (!Array.isArray(prev)) return prev;
    return prev.map((o) => {
      if (o.orderId !== orderId) return o;
      return {
        ...o,
        sellerChatId: o.sellerChatId ?? sellerChatId,
        buyerChatId: o.buyerChatId ?? buyerChatId,
        status: "paid",
        paidAt: Date.now(),
      };
    });
  });

  onOrderPaid?.(orderId);
};

/* ── CONFIRM PAYMENT ERROR ── */
const onConfirmPaymentError = ({ reason }) => {
  showToast?.(reason || "Failed to confirm payment.");
};

    /* ── OFFERS LIST ── */
    const onOffers = ({ offers, error }) => {
      if (error) {
        showToast?.(error);
        return;
      }
      setP2pOffers?.(offers || []);
    };

    /* ── CURRENCY UPDATE SUCCESS ── */
    const onUpdateCurrencySuccessSocket = ({ currency }) => {
      setSellerCurrency?.(currency ?? null);
      onUpdateCurrencySuccess?.({ currency });
    };

    /* ── CURRENCY UPDATE ERROR ── */
    const onUpdateCurrencyErrorSocket = ({ error }) => {
      onUpdateCurrencyError?.({ error });
    };

    /* ── SET PAYMENT DETAILS SUCCESS ── */
    const onSetPaymentDetailsSuccess = ({ currency, paymentDetails }) => {
      if (currency != null) setSellerCurrency?.(currency);
      if (Array.isArray(paymentDetails)) setSellerPaymentDetails?.(paymentDetails);
      onPaymentDetailsSaved?.({ currency, paymentDetails });
    };

    /* ── SET PAYMENT DETAILS ERROR ── */
    const onSetPaymentDetailsError = ({ error }) => {
      showToast?.(error || "Failed to save payment details");
    };

    /* ── FOLLOW SELLER RESULT ── */
    const onFollowSellerResultSocket = ({ success, sellerChatId, error }) => {
      if (!success) showToast?.(error || "Failed to follow seller");
      onFollowSellerResult?.({ success, sellerChatId, error });
    };

    /* ── NEW FOLLOWER (seller side) ── */
    const onNewFollowerSocket = ({ followerChatId }) => {
      onNewFollower?.({ followerChatId });
    };

    /* ── BINDINGS ── */
    socket.on("connect",                          onConnect);
    socket.on("p2p:ad-created",                   onAdCreatedSocket);
    socket.on("p2p:new-ad",                       onNewAdSocket);
    socket.on("p2p:ad-updated",                   onAdUpdatedSocket);
    socket.on("p2p:ad-deleted",                   onAdDeletedSocket);
    socket.on("p2p:delete-ad:error",              onDeleteAdErrorSocket);
    socket.on("p2p:update-ad:success",            onUpdateAdSuccessSocket);
    socket.on("p2p:update-ad:error",               onUpdateAdErrorSocket);
    socket.on("uswd:get-my-ad:success",           onGetMyAdSuccessSocket);   // ADD
    socket.on("uswd:get-my-ad:error",              onGetMyAdErrorSocket);    // ADD
    socket.on("p2p:require-payment-details",      onRequirePaymentDetails);
    socket.on("p2p:payment-details-saved",        onPaymentDetailsSavedSocket);
    socket.on("p2p:order-paid",                   onOrderPaidSocket);
    socket.on("p2p:order-completed",              onOrderCompletedSocket);
    socket.on("p2p:confirm-payment:error", onConfirmPaymentError);
    socket.on("p2p:committed-update",             onCommittedUpdate);
    socket.on("p2p:new-order",                    onNewOrder);
    socket.on("p2p:order-created",                onOrderCreatedSocket);
    socket.on("p2p:order-timer",                  onOrderTimer);
    socket.on("p2p:order-expired",                onOrderExpiredSocket);
    socket.on("p2p:offers",                       onOffers);
    socket.on("p2p:update-currency:success",      onUpdateCurrencySuccessSocket);
    socket.on("p2p:update-currency:error",        onUpdateCurrencyErrorSocket);
    socket.on("p2p:set-payment-details:success",  onSetPaymentDetailsSuccess);
    socket.on("p2p:set-payment-details:error",    onSetPaymentDetailsError);
    socket.on("p2p:follow-seller-result",         onFollowSellerResultSocket);
    socket.on("follow:new-follower",              onNewFollowerSocket);

    /* ── CLEANUP ── */
    return () => {
      socket.off("connect",                         onConnect);
      socket.off("p2p:ad-created",                  onAdCreatedSocket);
      socket.off("p2p:new-ad",                      onNewAdSocket);
      socket.off("p2p:ad-updated",                  onAdUpdatedSocket);
      socket.off("p2p:ad-deleted",                  onAdDeletedSocket);
      socket.off("p2p:delete-ad:error",             onDeleteAdErrorSocket);
      socket.off("p2p:update-ad:success",           onUpdateAdSuccessSocket);
      socket.off("p2p:update-ad:error",              onUpdateAdErrorSocket);
      socket.off("uswd:get-my-ad:success",          onGetMyAdSuccessSocket);   // ADD
      socket.off("uswd:get-my-ad:error",             onGetMyAdErrorSocket);    // ADD
      socket.off("p2p:require-payment-details",     onRequirePaymentDetails);
      socket.off("p2p:payment-details-saved",       onPaymentDetailsSavedSocket);
      socket.off("p2p:order-paid",                  onOrderPaidSocket);
      socket.off("p2p:order-completed",             onOrderCompletedSocket);
      socket.off("p2p:confirm-payment:error", onConfirmPaymentError);
      socket.off("p2p:committed-update",            onCommittedUpdate);
      socket.off("p2p:new-order",                   onNewOrder);
      socket.off("p2p:order-created",               onOrderCreatedSocket);
      socket.off("p2p:order-timer",                 onOrderTimer);
      socket.off("p2p:order-expired",                onOrderExpiredSocket);
      socket.off("p2p:offers",                       onOffers);
      socket.off("p2p:update-currency:success",     onUpdateCurrencySuccessSocket);
      socket.off("p2p:update-currency:error",       onUpdateCurrencyErrorSocket);
      socket.off("p2p:set-payment-details:success", onSetPaymentDetailsSuccess);
      socket.off("p2p:set-payment-details:error",   onSetPaymentDetailsError);
      socket.off("p2p:follow-seller-result",        onFollowSellerResultSocket);
      socket.off("follow:new-follower",              onNewFollowerSocket);
    };
  }, [
    socket,
    userId,
    myChatId,
    setP2pOffers,
    setP2POrders,
    setMyAds,
    setWallet,
    setOrderTimers,
    setSellerCurrency,
    setSellerPaymentDetails,
    showToast,
    onAdCreated,
    onOrderCreated,
    onOrderCompleted,
    onOrderExpired,
    onOrderPaid,
    onActiveOrders,
    onAdUpdated,
    onUpdateAdError,
    onMyAdLoaded,       // ADD
    onGetMyAdError,      // ADD
    onUpdateCurrencySuccess,
    onUpdateCurrencyError,
    onPaymentDetailsSaved,
    onFollowSellerResult,
    onNewFollower,
  ]);

  return {
    handleConfirmPayment,
  };
}